import React, { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import type { PortalWithdrawalLimitsConfig } from '../../../types';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { usePatchSuperAgentPortalWithdrawalLimits } from '../../../hooks/useApi';

type Props = {
  superAgentId: string;
  disabled?: boolean;
  limits?: PortalWithdrawalLimitsConfig | null;
  rawMaxPerTransaction?: number | string | null;
  rawMaxPerDay?: number | string | null;
};

function parseNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number.parseFloat(String(v));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatUsd(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

const SuperAgentPortalWithdrawalLimitsCard: React.FC<Props> = ({
  superAgentId,
  disabled,
  limits,
  rawMaxPerTransaction,
  rawMaxPerDay,
}) => {
  const patchLimits = usePatchSuperAgentPortalWithdrawalLimits();
  const systemTx = limits?.systemDefaults?.maxPerTransaction ?? 10000;
  const systemDay = limits?.systemDefaults?.maxPerDay ?? 50000;

  const initialUseSystem =
    limits?.isUsingSystemDefault ??
    (parseNum(rawMaxPerTransaction) == null && parseNum(rawMaxPerDay) == null);

  const [useSystemDefault, setUseSystemDefault] = useState(initialUseSystem);
  const [perTx, setPerTx] = useState(
    () => (parseNum(rawMaxPerTransaction) ?? limits?.maxPerTransaction ?? systemTx).toString()
  );
  const [perDay, setPerDay] = useState(
    () => (parseNum(rawMaxPerDay) ?? limits?.maxPerDay ?? systemDay).toString()
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setUseSystemDefault(initialUseSystem);
    setPerTx(
      initialUseSystem
        ? String(systemTx)
        : String(parseNum(rawMaxPerTransaction) ?? limits?.maxPerTransaction ?? systemTx)
    );
    setPerDay(
      initialUseSystem
        ? String(systemDay)
        : String(parseNum(rawMaxPerDay) ?? limits?.maxPerDay ?? systemDay)
    );
  }, [superAgentId, initialUseSystem, rawMaxPerTransaction, rawMaxPerDay, limits?.maxPerTransaction, limits?.maxPerDay, systemTx, systemDay]);

  const effectivePreview = useMemo(() => {
    if (useSystemDefault) {
      return { tx: systemTx, day: systemDay };
    }
    return { tx: parseNum(perTx) ?? systemTx, day: parseNum(perDay) ?? systemDay };
  }, [useSystemDefault, perTx, perDay, systemTx, systemDay]);

  const handleSave = async () => {
    setLocalError(null);
    if (useSystemDefault) {
      await patchLimits.mutateAsync({
        superAgentId,
        max_portal_withdrawal_per_transaction: null,
        max_portal_withdrawal_per_day: null,
      });
      return;
    }
    const tx = parseNum(perTx);
    const day = parseNum(perDay);
    if (tx == null || day == null) {
      setLocalError('Enter positive amounts for both limits, or use system defaults.');
      return;
    }
    if (day < tx) {
      setLocalError('Daily limit must be greater than or equal to the per-request limit.');
      return;
    }
    await patchLimits.mutateAsync({
      superAgentId,
      max_portal_withdrawal_per_transaction: tx,
      max_portal_withdrawal_per_day: day,
    });
  };

  return (
    <div className="rounded-md border p-4 space-y-4 text-sm">
      <div>
        <p className="font-medium text-foreground">USD wallet withdrawal limits (USD)</p>
        <p className="text-xs text-muted-foreground mt-1">
          Sets the maximum amount this super agent, and every agent and shop under them, can withdraw from their online
          USD balance to crypto. Agents and shops can view these limits but cannot change them.
        </p>
      </div>

      {useSystemDefault ? (
        <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50/80 p-3 text-xs text-blue-900">
          <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p>
            Using system defaults: up to {formatUsd(systemTx)} per request, up to {formatUsd(systemDay)} total per day.
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Currently applying: up to {formatUsd(effectivePreview.tx)} per request, up to{' '}
          {formatUsd(effectivePreview.day)} per day.
        </p>
      )}

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={useSystemDefault}
          disabled={disabled || patchLimits.isLoading}
          onChange={(e) => setUseSystemDefault(e.target.checked)}
        />
        <span>Use system default limits</span>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Max per withdrawal (USD)
          </label>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={perTx}
            disabled={disabled || useSystemDefault || patchLimits.isLoading}
            onChange={(e) => setPerTx(e.target.value)}
            placeholder="e.g. 10000"
          />
          <p className="text-xs text-muted-foreground mt-1">Maximum in a single withdrawal request.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Max per day (USD)
          </label>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={perDay}
            disabled={disabled || useSystemDefault || patchLimits.isLoading}
            onChange={(e) => setPerDay(e.target.value)}
            placeholder="e.g. 50000"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Total of all withdrawals in one day (pending and completed).
          </p>
        </div>
      </div>

      {localError ? <p className="text-xs text-destructive">{localError}</p> : null}

      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled || patchLimits.isLoading}
        onClick={() => void handleSave()}
      >
        {patchLimits.isLoading ? 'Saving…' : 'Save withdrawal limits'}
      </Button>
    </div>
  );
};

export default SuperAgentPortalWithdrawalLimitsCard;
